/* ===========================================================================
   Keyboard support for Mailchimp's "Lead The Pack!" signup popup (AC-1980)
   ---------------------------------------------------------------------------
   Loaded from layout/theme.liquid on every template. The Mailchimp app embed
   renders its popup inside an open shadow root on `div.mcforms-wrapper`
   (appended to <body> once, after cookie consent). On Live, Escape did
   nothing and Enter on the Close X did nothing (only Space worked), so a
   keyboard user could not leave the dialog (WCAG 2.1.1 / 2.1.2). The real fix
   belongs in Mailchimp; this is a theme-side mitigation:
   - Escape, while a modal step is showing, clicks that step's own Close X.
   - Enter on a close-type control clicks it, exactly once.
   - Enter on an advance control ("CLAIM 10% Off", "Continue", the 2nd Chance
     "Claim 10% off") clicks it, exactly once; Mailchimp ignored Enter there.
   - After a close, focus goes back to the element focused before focus
     entered the popup (or focused at close time if it never entered), or to
     the top of the page (next Tab: "Skip to content"). Mailchimp focuses the
     2nd Chance pill or the Reminder Bar it shows next, so focus inside the
     popup after a close counts as lost, and is pulled back out again for a
     short while unless the user has pressed a key or a pointer since.
   Handled keys are stopped in the window capture phase, before the popup and
   before page-wide handlers (Judge.me's Escape moves focus to "Write a review"
   on PDPs; the cart drawer closes on Escape).

   The ids are element ids of experience variation 1069995, rendered as
   `#el_<id>` inside the shadow root, read from
   form-assets.mailchimp.com/snippet/config/22723391/variation/1069995
   (steps[].elements[].id; the config's own CSS targets `#el__Z9fJVxDzx>svg`).
   A Mailchimp redesign or a new variation changes them SILENTLY: Escape and
   Enter stop working again and nothing errors. Re-read the config.
   =========================================================================== */
(function () {
  "use strict";

  // Close X of each modal step: Offer (rSjbJFbtI), Email Capture (kYcju490uK),
  // Confirmation (nIIYNTxBT).
  var MODAL_CLOSE_IDS = ["_Z9fJVxDzx", "VSwRDZcpDr", "jmMFYmPq_D"];
  // Every control whose action is `close`: the modal Close X ids, Offer
  // "No, thanks", Confirmation "Back to site", 2nd Chance pill Close X.
  // The Reminder Bar Close X (wSnOOcnhtG) is left to Mailchimp.
  var CLOSE_CONTROL_IDS = MODAL_CLOSE_IDS.concat(["UxXZwxfmucn", "e7lSPJOunH", "RZdU1ugX0K"]);
  // Controls whose action is `goto-step` (Continue also `collect-data`): Offer
  // "CLAIM 10% Off", Email Capture "Continue", 2nd Chance "Claim 10% off".
  // The email input is a sibling of Continue, so Enter in it is not handled.
  var ADVANCE_CONTROL_IDS = ["yCpWglFnIkh", "W9JaCN2ToO", "S0N6DIGNFV"];
  var CLOSE_CHECKS = 10; // wait for the modal step to hide
  var WATCH_CHECKS = 15; // then keep focus out of the popup
  var RESTORE_INTERVAL_MS = 100;

  var session = null; // { host, returnTo } from the first focusin into a popup
  var pending = null; // { host, returnTo, closed, checks } after a close
  var swallowKeyup = null;
  var restoreTimer = 0;
  var restoreFrame = 0;

  function hostFromPath(path) {
    for (var i = 0; i < path.length; i++) {
      var node = path[i];
      if (node.nodeType === 1 && node.shadowRoot && node.classList.contains("mcforms-wrapper")) return node;
    }
    return null;
  }

  function isShown(el) {
    return (
      !!el && el.isConnected && el.getClientRects().length > 0 && window.getComputedStyle(el).visibility !== "hidden"
    );
  }

  function visibleModalClose() {
    var hosts = document.querySelectorAll(".mcforms-wrapper");
    for (var h = 0; h < hosts.length; h++) {
      var root = hosts[h].shadowRoot;
      if (!root) continue;
      for (var i = 0; i < MODAL_CLOSE_IDS.length; i++) {
        var el = root.querySelector('[id="el_' + MODAL_CLOSE_IDS[i] + '"]');
        if (isShown(el)) return { host: hosts[h], control: el };
      }
    }
    return null;
  }

  function controlInPath(path, host) {
    for (var i = 0; i < path.length && path[i] !== host; i++) {
      var node = path[i];
      if (node.nodeType === 1 && node.id && node.id.indexOf("el_") === 0) {
        var id = node.id.slice(3);
        if (CLOSE_CONTROL_IDS.indexOf(id) !== -1) return { node: node, closes: true };
        if (ADVANCE_CONTROL_IDS.indexOf(id) !== -1) return { node: node, closes: false };
      }
    }
    return null;
  }

  function activate(el) {
    if (typeof el.click === "function") {
      el.click();
    } else {
      // SVG elements have no click() in every browser.
      el.dispatchEvent(new MouseEvent("click", { bubbles: true, cancelable: true, composed: true }));
    }
  }

  function swallow(event) {
    event.preventDefault();
    event.stopImmediatePropagation();
    swallowKeyup = event.key;
  }

  function inPopup(el) {
    return !!el && el.nodeType === 1 && !!el.closest(".mcforms-wrapper");
  }

  function focusOnBody() {
    var active = document.activeElement;
    return !active || active === document.body || active === document.documentElement;
  }

  function focusPageStart() {
    // Focusing and removing a node at the start of <body> leaves focus on body
    // and moves the sequential focus starting point there, so the next Tab
    // reaches "Skip to content" instead of the 2nd Chance pill.
    var anchor = document.createElement("span");
    anchor.tabIndex = -1;
    document.body.insertBefore(anchor, document.body.firstChild);
    anchor.focus({ preventScroll: true });
    anchor.parentNode.removeChild(anchor);
  }

  function restoreFocus(target) {
    if (target && !inPopup(target) && isShown(target)) {
      target.focus({ preventScroll: true });
      if (document.activeElement === target) return;
    }
    focusPageStart();
  }

  function returnTarget(host) {
    if (session && session.host === host) return session.returnTo;
    var active = document.activeElement;
    return focusOnBody() || inPopup(active) ? null : active;
  }

  function cancelRestore() {
    pending = null;
    window.cancelAnimationFrame(restoreFrame);
    window.clearTimeout(restoreTimer);
  }

  // Runs after a close control was activated by any means (Enter, Space,
  // pointer). Waits for Mailchimp to hide the modal step, then moves focus out
  // of the popup (or off body) to the return target. For WATCH_CHECKS more
  // ticks it undoes a later Mailchimp focus move into the popup; focus on body
  // alone is not re-restored then, the fallback leaves it there.
  function scheduleRestore(host) {
    var returnTo = pending && pending.host === host && !pending.closed ? pending.returnTo : returnTarget(host);
    cancelRestore();
    var watch = (pending = { host: host, returnTo: returnTo, closed: false, checks: 0 });
    function next(limit) {
      if (++watch.checks < limit) restoreTimer = window.setTimeout(check, RESTORE_INTERVAL_MS);
      else pending = null;
    }
    function check() {
      if (pending !== watch) return;
      if (visibleModalClose()) {
        // Still closing, or a modal step is open again: never pull focus out of it.
        if (watch.closed) pending = null;
        else next(CLOSE_CHECKS);
        return;
      }
      if (!watch.closed) {
        watch.closed = true;
        watch.checks = 0;
        if (inPopup(document.activeElement) || focusOnBody()) restoreFocus(watch.returnTo);
      } else if (inPopup(document.activeElement)) {
        restoreFocus(watch.returnTo);
      }
      next(WATCH_CHECKS);
    }
    restoreFrame = window.requestAnimationFrame(check);
  }

  function onKeydown(event) {
    // Any new key press is the user acting: stop pulling focus back.
    if (pending && !event.repeat) cancelRestore();
    if (event.repeat && swallowKeyup === event.key) {
      // Auto-repeat of a key that already closed the popup: keep it away from
      // the page until the key is released.
      swallow(event);
      return;
    }
    swallowKeyup = null;
    if (event.defaultPrevented || event.isComposing) return;

    if (event.key === "Escape" || event.key === "Esc") {
      var open = visibleModalClose();
      if (!open) return; // the 2nd Chance pill alone is not modal
      swallow(event);
      if (event.repeat) return;
      activate(open.control.querySelector("button, a[href], [role='button']") || open.control);
      scheduleRestore(open.host);
      return;
    }

    if (event.key !== "Enter" || event.altKey || event.ctrlKey || event.metaKey) return;
    var path = event.composedPath();
    var host = hostFromPath(path);
    if (!host) return;
    var control = controlInPath(path, host);
    if (!control) return;
    // preventDefault also cancels a native button's own Enter activation, so
    // the click below is the only one; a held Enter never clicks again.
    swallow(event);
    if (event.repeat) return;
    activate(path[0].nodeType === 1 ? path[0] : control.node);
    if (control.closes) scheduleRestore(host);
  }

  function onKeyup(event) {
    if (swallowKeyup !== null && event.key === swallowKeyup) {
      swallowKeyup = null;
      event.preventDefault();
      event.stopImmediatePropagation();
      return;
    }
    if (event.key === " " || event.key === "Spacebar") onCloseIntent(event);
  }

  function onCloseIntent(event) {
    var path = event.composedPath();
    var host = hostFromPath(path);
    var control = host && controlInPath(path, host);
    if (control && control.closes) scheduleRestore(host);
  }

  function onFocusin(event) {
    var host = hostFromPath(event.composedPath());
    if (!host) {
      session = null;
      return;
    }
    if (session && session.host === host) return;
    var previous = event.relatedTarget;
    var outside = previous && previous.nodeType === 1 && previous !== host && previous !== document.body;
    session = { host: host, returnTo: outside ? previous : null };
  }

  window.addEventListener("keydown", onKeydown, true);
  window.addEventListener("keyup", onKeyup, true);
  window.addEventListener("click", onCloseIntent, true);
  window.addEventListener("pointerdown", cancelRestore, true);
  window.addEventListener("focusin", onFocusin, true);
})();
